// src/utils/applySiteSettings.js
import getImageUrl from "./getImageUrl";

const DEFAULT_PRIMARY = "#FF6A59";
const DEFAULT_SECONDARY = "#4CBC9A";

/** Parse hex to r,g,b (0-255). Returns null if invalid. */
function hexToRgb(hex) {
  if (!hex || typeof hex !== "string") return null;
  const h = hex.replace(/^#/, "");
  if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(h)) return null;
  const len = h.length === 3 ? 1 : 2;
  return {
    r: parseInt(h.slice(0, len), 16) * (len === 1 ? 17 : 1),
    g: parseInt(h.slice(len, len * 2), 16) * (len === 1 ? 17 : 1),
    b: parseInt(h.slice(len * 2, len * 3), 16) * (len === 1 ? 17 : 1),
  };
}

/** Darken hex by a factor 0..1 (e.g. 0.15 = 15% darker). */
function darkenHex(hex, factor = 0.15) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const f = 1 - Math.min(Math.max(factor, 0), 1);
  const r = Math.round(rgb.r * f);
  const g = Math.round(rgb.g * f);
  const b = Math.round(rgb.b * f);
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}

/** Generate rgba(r,g,b, alpha) for a hex color. */
function hexToRgba(hex, alpha) {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(0,0,0,${alpha})`;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

/**
 * Apply primary and secondary theme colors to the document (body).
 * Sets --primary, --secondary, --secondary-dark, --primary-hover, --primary-dark,
 * --title (same as primary for headings), and rgba variants.
 */
export function applyThemeColors(primaryHex, secondaryHex) {
  const root = document.body || document.documentElement;
  const primary = primaryHex || DEFAULT_PRIMARY;
  const secondary = secondaryHex || DEFAULT_SECONDARY;

  root.style.setProperty("--primary", primary);
  root.style.setProperty("--secondary", secondary);
  root.style.setProperty("--primary-hover", darkenHex(primary, 0.12));
  root.style.setProperty("--primary-dark", darkenHex(primary, 0.25));
  root.style.setProperty("--secondary-dark", darkenHex(secondary, 0.2));
  root.style.setProperty("--title", primary);

  for (let i = 1; i <= 9; i++) {
    root.style.setProperty(`--rgba-primary-${i}`, hexToRgba(primary, i * 0.1));
    root.style.setProperty(`--rgba-secondary-${i}`, hexToRgba(secondary, i * 0.1));
  }
}

export function applySiteSettings(data) {
  if (!data) return;

  document.title = data.site_title || "My Website";

  const faviconUrl = getImageUrl(data?.site_favicon?.path);
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = faviconUrl;

  applyThemeColors(data.back_primary_color, data.back_secondary_color);
}

export function cacheSiteSettings(data) {
  if (!data) return;
  localStorage.setItem("siteSettings", JSON.stringify(data));
  localStorage.setItem("siteFavicon", getImageUrl(data?.site_favicon?.path));
  localStorage.setItem("siteLogo", getImageUrl(data?.site_logo?.path));
  localStorage.setItem("siteTitle", data.site_title);
  localStorage.setItem("primaryColor", data.back_primary_color);
  localStorage.setItem("secondaryColor", data.back_secondary_color);
}
